<script setup lang="ts">
import { onBeforeMount, ref, provide, watch, computed } from "vue";
import { useApiClient } from "@/api/client";
import { useRoute } from "vue-router";
import DocumentDO = App.Data.Explorer.DocumentDO;
import SegmentInsightGraph from "@/modules/segments/components/SegmentInsightGraph.vue";
import { loadingWrapperAll } from "@/api/helpers/loadingWrapper";
import SnapshotSelectionWrapper from "@/modules/explorer/components/SnapshotSelectionWrapper.vue";
import dayjs from "dayjs";
import { useSegmentBuilderStore } from "@/modules/segments/store";
import { useBaseStore } from "@/stores/base";
import { t } from "@/plugins/i18n";

provide("fetchDataPrefix", "segments/");

const requiredPermissions = [
	"can_view_property_segment_size",
	"can_view_property_retention_12m",
	"can_view_property_retention_single_cohort",
	"can_view_property_periodsubjectdatemonth",
	"can_view_property_date_comparison",
];

const segmentSizeDocument = ref<DocumentDO>();
const segmentRetentionDocument = ref<DocumentDO>();
const segmentRetentionCohortDocument = ref<DocumentDO>();

const resource_uuid: string | null =
	useRoute().params.resource_uuid !== ""
		? String(useRoute().params.resource_uuid)
		: null;

const missingPermissions = computed(() => {
	const userPermissions = useBaseStore().currentUser?.derivedPermissions ?? [];

	return requiredPermissions.filter(
		(permission) => !userPermissions.includes(permission)
	);
});

const isLoading = ref<boolean>(true);
const isLoadingCohort = ref<boolean>(true);
const lastUpdated = ref<number>(1);
const compareEnabled = ref<boolean>(false);

const fetchSegmentRetentionCohort = async () => {
	isLoadingCohort.value = true;

	const response = await useApiClient(
		"segments"
	).getDocumentForSegmentRetentionCohortInsights(
		resource_uuid,
		cohortStartDate.value,
		cohortStartDateParsed.value,
		cohortNoOfMonths.value,
		compareEnabled.value
	);

	if (response.isOk()) {
		segmentRetentionCohortDocument.value = response.value;
	}

	isLoadingCohort.value = false;
};

const getDocumentForSegmentSizeInsights = async () => {
	isLoading.value = true;

	const [segmentSizeResponse, segmentRetentionResponse] =
		await loadingWrapperAll([
			useApiClient("segments").getDocumentForSegmentSizeInsights(
				resource_uuid,
				compareEnabled.value
			),
			useApiClient("segments").getDocumentForSegmentRetentionInsights(
				resource_uuid,
				compareEnabled.value
			),
		]);

	if (segmentSizeResponse.isOk()) {
		segmentSizeDocument.value = segmentSizeResponse.value;
	}

	if (segmentRetentionResponse.isOk()) {
		segmentRetentionDocument.value = segmentRetentionResponse.value;
	}

	await fetchSegmentRetentionCohort();

	isLoading.value = false;
};

const isStartDateSelectionVisible = ref(false);

const cohortStartDateParsed = ref<Date>(
	dayjs().subtract(12, "month").startOf("month").toDate()
);
const cohortStartDate = ref<string>("now-12M/M");

const cohortNoOfMonths = ref<number>(12);

watch(cohortStartDate, () => {
	fetchSegmentRetentionCohort();
});

watch(cohortNoOfMonths, () => {
	fetchSegmentRetentionCohort();
});

watch(compareEnabled, () => {
	getDocumentForSegmentSizeInsights();
});

const hasDependencyOnLostCustomerSegment = computed(
	() =>
		useSegmentBuilderStore().segmentBuilderState.segment
			?.has_dependency_on_lost_customer_segment
);

onBeforeMount(async () => {
	if (missingPermissions.value.length === 0) {
		await getDocumentForSegmentSizeInsights();
	}
});
</script>

<template>
	<div>
		<v-alert
			icon="fa: fa-duotone fa-shield-halved"
			color="error"
			border="start"
			v-if="missingPermissions.length > 0"
			prominent
		>
			<div class="pl-5">
				<div>
					<v-card-title class="pl-0"> Invalid permissions </v-card-title>
				</div>
				<div>
					You do not have all the required permissions to view the graphs
					displayed on this page, missing permissions are:
				</div>
				<ul class="mt-5">
					<li v-for="permission in missingPermissions" :key="permission">
						<strong>
							{{
								t(
									`properties.${permission.replace(
										"can_view_property_",
										""
									)}.title`
								)
							}}</strong
						>
						(
						<span class="text-caption">
							{{
								t(
									`properties.${permission.replace(
										"can_view_property_",
										""
									)}.description`
								)
							}}</span
						>
						)
					</li>
				</ul>
			</div>
		</v-alert>
	</div>
	<v-fade-transition>
		<div class="pt-5" v-if="!isLoading && missingPermissions.length === 0">
			<div class="d-flex">
				<v-spacer />
				<v-checkbox
					v-model="compareEnabled"
					color="primary"
					false-value="false"
					true-value="true"
					label="Show comparison to last year"
					hide-details
				/>
			</div>

			<v-alert
				icon="fa: fa-light fa-triangle-exclamation"
				color="warning"
				v-if="useSegmentBuilderStore().segmentBuilderState.segmentHasChanges"
				class="mb-5"
				prominent
			>
				<div>
					<div>
						<strong
							>You’ve made changes to your segment in the builder, but they
							haven’t been saved yet.</strong
						>
					</div>
					<div>
						To see an updated view of the data in the graphs below, please save
						your segment first.
					</div>
				</div>
			</v-alert>

			<v-card class="mb-5">
				<v-toolbar dense flat>
					<v-toolbar-title>
						<span class="text-subheading">Segment Size</span>
					</v-toolbar-title>
				</v-toolbar>

				<v-card-text>
					<v-sheet class="pb-5">
						<div style="height: 500px">
							<segment-insight-graph
								:last-updated="lastUpdated"
								:document="segmentSizeDocument"
								:is-loading-document="isLoading"
							/>
						</div>
					</v-sheet>
					<v-alert type="info" color="blue-grey-lighten-1" closable>
						<div>
							<p>
								This chart illustrates the
								<strong>monthly progression of your segment size</strong> over
								the past 12 months, up to now.
							</p>

							<p class="pt-2">
								Each bar represents the total number of customers in the segment
								for that specific month.
							</p>
							<p class="pt-2">
								Use this trend to evaluate the segment’s health over time,
								assess the impact of campaigns, or track seasonality. This helps
								you understand whether your audience is growing, shrinking, or
								remaining stable.
							</p>
						</div>
					</v-alert>
				</v-card-text>
			</v-card>

			<v-card class="mb-5" v-if="!hasDependencyOnLostCustomerSegment">
				<v-toolbar dense flat>
					<v-toolbar-title>
						<span class="text-subheading">Retention Percentage</span>
					</v-toolbar-title>
				</v-toolbar>

				<v-card-text>
					<v-sheet class="pb-5">
						<div style="height: 500px">
							<segment-insight-graph
								:last-updated="lastUpdated"
								:document="segmentRetentionDocument"
								:is-loading-document="isLoading"
							/>
						</div>
					</v-sheet>
					<v-alert
						class="px-5 justify-between"
						color="blue-grey-lighten-1"
						closable
					>
						<div>
							<p>
								This chart visualizes
								<strong>customer retention within your segment over time</strong
								>. Each bar represents a month and indicates the percentage of
								customers who remained in the segment compared to exactly 12
								months earlier. For example:
							</p>

							<div class="pt-2">
								<ul class="pl-4 py-2">
									<li>
										<strong>July 2024</strong> compares customers in the segment
										that month to those in <strong>July 2023</strong>.
									</li>
									<li>
										<strong>April 2025</strong> reflects retention from
										<strong>April 2024</strong>.
									</li>
								</ul>
							</div>

							<p>
								Keep in mind: the reference point shifts with each bar — it's
								always a 12-month look-back from that specific month. This
								moving window helps you identify long-term trends and measure
								how consistently your segment retains its members.
							</p>
						</div>
					</v-alert>
				</v-card-text>
			</v-card>

			<v-card class="mb-5" v-if="!hasDependencyOnLostCustomerSegment">
				<v-toolbar dense flat>
					<v-toolbar-title>
						<span class="text-subheading">Retention Cohort Analysis</span>
					</v-toolbar-title>
					<div class="d-flex">
						<v-menu
							v-model="isStartDateSelectionVisible"
							:close-on-content-click="false"
							location="bottom right"
						>
							<template v-slot:activator="{ props }">
								<v-btn
									prepend-icon="fa: fa-light fa-calendar-range"
									v-bind="props"
									class="ml-3 mr-3"
									variant="flat"
								>
									Change cohort reference date
								</v-btn>
							</template>
							<v-card min-width="400">
								<snapshot-selection-wrapper
									v-model="isStartDateSelectionVisible"
									v-model:period_measurement="cohortStartDate"
									v-model:period_measurement_parsed="cohortStartDateParsed"
								/>
							</v-card>
						</v-menu>
						<div class="px-3 d-flex">
							<div class="pt-2 pr-3 text-caption">Add no. of months:</div>
							<div>
								<v-select
									v-model="cohortNoOfMonths"
									color="primary"
									variant="outlined"
									density="compact"
									:items="Array.from({ length: 12 }, (_, i) => i + 1)"
									hide-details
								></v-select>
							</div>
						</div>
					</div>
				</v-toolbar>

				<v-card-text>
					<v-sheet class="pb-5">
						<div style="height: 500px" v-if="!isLoadingCohort">
							<segment-insight-graph
								:last-updated="lastUpdated"
								:document="segmentRetentionCohortDocument"
								:is-loading-document="isLoadingCohort"
							/>
						</div>
						<div v-else style="height: 500px">
							<!--							height placeholder-->
						</div>
					</v-sheet>
					<v-alert
						color="blue-grey-lighten-1"
						class="px-5 justify-between"
						closable
					>
						<div>
							<p>
								This chart presents a
								<strong>retention cohort analysis</strong>, allowing you to
								track how well a specific group of users (a cohort) remains
								active over time.
							</p>

							<div class="pt-2">
								<ul class="pl-4 py-2">
									<li>
										The first bar (e.g. <strong>July 2024</strong>) represents
										the initial size of the cohort — the number of users who
										entered the segment in that month.
									</li>
									<li>
										Each bar after that shows how many of those original users
										are still present in the segment during the months that
										follow.
									</li>
								</ul>
							</div>

							<p class="pt-2">
								In other words, this chart shows how a fixed starting group of
								users “ages” across time, helping you identify
								<strong>long-term retention patterns</strong>.
							</p>

							<p>
								This is useful for spotting drop-off points, evaluating
								onboarding effectiveness, or comparing cohort performance month
								over month.
							</p>
						</div>
					</v-alert>
				</v-card-text>
			</v-card>
		</div>
	</v-fade-transition>
</template>

<style scoped lang="scss"></style>
